import React, { useState } from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { processSteps } from '../../mock';
import ContactPopup from '../../components/ContactPopup';

const CopyrightPortfolio = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const benefits = [
    "Centralized portfolio tracking",
    "Registration status monitoring",
    "Renewal deadline management",
    "License agreement oversight",
    "Revenue optimization strategies",
    "Infringement monitoring",
    "Portfolio valuation services",
    "Strategic acquisition guidance"
];

  const faqs = [
    {
        "q": "What is copyright portfolio management?",
        "a": "Portfolio management involves tracking, maintaining, and strategically managing multiple copyrighted works to maximize their value and ensure ongoing protection."
    },
    {
        "q": "Why is portfolio management important?",
        "a": "Proper management prevents loss of rights, identifies revenue opportunities, ensures compliance, and provides strategic insights for business decisions."
    },
    {
        "q": "What records should I maintain?",
        "a": "Maintain registration certificates, assignment documents, license agreements, creation dates, authorship records, and renewal information for all works."
    },
    {
        "q": "How often should I audit my portfolio?",
        "a": "Annual audits are recommended to verify registrations, review licenses, identify new works needing protection, and assess portfolio value."
    },
    {
        "q": "Can you help with international copyrights?",
        "a": "Yes, we manage international copyright portfolios, coordinate foreign registrations, and ensure compliance across multiple jurisdictions."
    },
    {
        "q": "How do I maximize portfolio value?",
        "a": "Maximize value through strategic licensing, proper registration, active enforcement, portfolio pruning, and identifying high-value works for monetization."
    }
];

  return (
    <div className="min-h-screen bg-slate-950">
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center bg-amber-500/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <span className="text-sm text-amber-500 font-medium">Copyright Services</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Copyright Portfolio Management</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Comprehensive copyright portfolio management services for creators, publishers, and businesses with extensive creative assets. Maximize value and minimize risk across your entire portfolio.
            </p>
            <Button 
              size="lg" 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-xl transform transition-transform hover:scale-105"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Key Benefits</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 bg-slate-900/40 border border-white/20 rounded-lg p-4 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all">
                <CheckCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Streamlined Process</h2>
          </div>
          <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step) => (
              <div key={step.step} className="relative">
                <div className="bg-slate-900 border border-white/20 rounded-xl p-8 h-full hover:border-white/40 hover:shadow-xl hover:shadow-white/10 hover:scale-[1.02] transition-all group">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white w-16 h-16 rounded-full flex items-center justify-center font-black text-2xl mb-6 shadow-xl group-hover:scale-110 transition-transform">
                    {step.step}
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="md:hidden overflow-x-auto scrollbar-hide">
            <div className="flex gap-4 pb-4" style={{ scrollSnapType: 'x mandatory' }}>
              {processSteps.map((step) => (
                <div key={step.step} className="flex-shrink-0 w-[300px]" style={{ scrollSnapAlign: 'center' }}>
                  <div className="bg-slate-900 border border-white/20 rounded-xl p-6 h-full">
                    <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white w-14 h-14 rounded-full flex items-center justify-center font-black text-xl mb-4 shadow-xl">
                      {step.step}
                    </div>
                    <h3 className="text-white font-bold text-lg mb-3">{step.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        <div className="absolute top-10 right-10 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-amber-500 font-semibold uppercase tracking-wider text-sm">Got Questions?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              Frequently Asked
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Questions</span>
            </h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <Accordion key={index} type="single" collapsible>
                <AccordionItem value={"item-" + index} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-2 hover:border-white/40 hover:shadow-xl hover:shadow-white/10 transition-all group">
                  <AccordionTrigger className="text-white font-bold text-left hover:text-amber-400 py-6 group-hover:scale-[1.01] transition-all">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <span className="text-amber-400 font-black text-lg">{index + 1}</span>
                      </div>
                      <span className="flex-1">{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pl-14 pr-4 pb-6 leading-relaxed">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Button 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-xl hover:shadow-amber-500/30 hover:scale-105 transition-all px-8"
            >
              Contact Our Experts
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-white/20 rounded-2xl p-12 shadow-2xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Let our experts help you with professional copyright portfolio management services.
            </p>
            <Button 
              size="lg" 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-xl transform transition-transform hover:scale-105"
            >
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default CopyrightPortfolio;
